import {
  useCallback,
  useState,
  useRef,
  useMemo,
  RefObject,
  Dispatch,
  SetStateAction,
  useEffect,
  MutableRefObject,
} from 'react'
import { MentionComponent } from '@syncfusion/ej2-react-dropdowns'
import {
  ActionBeginEventArgs,
  RichTextEditorComponent,
} from '@syncfusion/ej2-react-richtexteditor'
import { useAppSelector } from '@store/hooks'
import { getFullName } from '@utils/fullname.util'

export interface IEditorInput {
  text: string
  htmlContent: string
}

export interface IInlineTEProps {
  handleAIFeaturesButtons: (command: string) => void
  handleClearBtn: () => void
  handleRightButtonsAction: (key: string) => void
  handleToolbarActions: (buttonName: string) => void
  isRecording: boolean
  mentionRef: RefObject<MentionComponent>
  onActionBegin: (e: ActionBeginEventArgs) => void
  onCreated: () => void
  onTextChange: () => void
  peopleList: { name: string }[]
  saveCaretPosition: () => void
  setVariablePopupOpen: (open: boolean) => void
  showVariables: boolean
  toolbarRef: RefObject<RichTextEditorComponent> | null
  variableRef: RefObject<MentionComponent>
}

const useInlineTextEditor = (
  editorInput: IEditorInput,
  enableEnterNextLine: boolean,
  hideToolbar: boolean,
  isAiEnabled: boolean,
  setEditorInput: Dispatch<SetStateAction<IEditorInput>>,
  setIsAiEnabled: Dispatch<SetStateAction<boolean>>,
  handleSend: () => void,
  toolbarRef: RefObject<RichTextEditorComponent> | null,
  isEditing?: boolean,
  onSaveEdit?: () => void,
  setIsEditing?: Dispatch<SetStateAction<boolean>>,
  selectionRef?: MutableRefObject<Range | null>,
  setAiAction?: Dispatch<SetStateAction<string>>,
  autoFocusKey?: string,
): IInlineTEProps => {
  const focusFrame = useRef<number | null>(null)
  const requestComposerFocus = useCallback((): void => {
    if (!autoFocusKey) return
    if (focusFrame.current !== null) cancelAnimationFrame(focusFrame.current)
    // Wait for the cleared draft or newly selected editor to finish rendering.
    focusFrame.current = requestAnimationFrame(() => {
      focusFrame.current = null
      const editor = toolbarRef?.current
      if (!editor || editor.readonly || !editor.element?.isConnected) return
      if (
        document.activeElement?.closest('[role="dialog"], [aria-modal="true"]')
      )
        return
      editor.focusIn()
    })
  }, [autoFocusKey, toolbarRef])

  useEffect(() => {
    requestComposerFocus()
    return () => {
      if (focusFrame.current !== null) cancelAnimationFrame(focusFrame.current)
      focusFrame.current = null
    }
  }, [requestComposerFocus])

  const patients = useAppSelector((state) => state.patient.data)
  const [isRecording, setIsRecording] = useState<boolean>(false)
  const [isVariablePopupOpen, setIsVariablePopupOpen] = useState<boolean>(false)
  const [showVariables, setShowVariables] = useState<boolean>(false)
  const mentionRef = useRef<MentionComponent>(null)
  const variableRef = useRef<MentionComponent>(null)

  const peopleList = useMemo(() => {
    return patients.map((patient) => ({ name: getFullName(patient) }))
  }, [patients])

  const setVariablePopupOpen = (open: boolean): void =>
    setIsVariablePopupOpen(open)

  const activeToolbarKey = isAiEnabled
    ? 'ai'
    : isVariablePopupOpen
      ? 'variable'
      : null

  useEffect(() => {
    if (hideToolbar || !toolbarRef?.current) return
    const toolbar = toolbarRef.current.getToolbar()
    if (!toolbar) return
    const customKeys = ['ai', 'variable']
    customKeys.forEach((key) => {
      const btn = toolbar.querySelector(`.custom-toolbar-button.${key}`)
      const toolbarItem = btn?.closest('.e-toolbar-item')
      if (toolbarItem) {
        if (key === activeToolbarKey) {
          toolbarItem.classList.add('e-active')
        } else {
          toolbarItem.classList.remove('e-active')
        }
      }
    })
  }, [activeToolbarKey, hideToolbar, toolbarRef])

  const handleToolbarActions = (buttonName: string): void => {
    if (toolbarRef) {
      const ref = toolbarRef.current

      switch (buttonName) {
        case 'variable':
          {
            if (variableRef.current && ref) {
              if (ref.getHtml() === '<p><br></p>') ref.value = '<p>&nbsp;</p>'
              variableRef.current.showPopup()
            }
          }
          break
        case 'mention':
          if (mentionRef.current && ref) {
            if (ref.getHtml() === '<p><br></p>') ref.value = '<p>&nbsp;</p>'
            mentionRef.current.showPopup()
          }
          break
        case 'ai':
          {
            const enablingAi = !isAiEnabled
            setIsAiEnabled((prevState) => !prevState)
            if (enablingAi) {
              if (toolbarRef.current) toolbarRef.current.value = ''
              setEditorInput({ text: '', htmlContent: '' })
            }
          }
          break
        default:
          break
      }
    }
  }

  const onActionBegin = (e: ActionBeginEventArgs): void => {
    if (e.requestType === 'EnterAction') {
      if (enableEnterNextLine) {
        e.cancel = false
      } else {
        e.cancel = true
        if (!autoFocusKey) toolbarRef?.current?.focusOut()
      }
    }
  }

  const handleClearBtn = (): void => {
    setEditorInput({
      text: '',
      htmlContent: '',
    })
    setAiAction?.('custom')
    if (isAiEnabled) setIsAiEnabled(false)
    if (isEditing && setIsEditing) setIsEditing(false)
  }

  const handleRecordAudio = (): void => {
    setIsRecording(!isRecording)
  }

  const handleRightButtonsAction = (key: string): void => {
    switch (key) {
      case 'send':
        {
          handleSend()
          requestComposerFocus()
          if (isEditing && onSaveEdit) {
            onSaveEdit()
          }
        }
        break
      case 'mic':
        handleRecordAudio()
        break
      default:
        break
    }
  }

  const handleAIFeaturesButtons = (command: string): void => {
    let newInputPrompt = ''
    switch (command.toLowerCase()) {
      case 'reply':
        newInputPrompt = 'Generate a response for the following message:\n '
        break
      case 'summarize':
        newInputPrompt = `Summarize the message below. Make 
          sure to retain the same context with shorter number of characters.\n `
        break
    }

    if (toolbarRef?.current) toolbarRef.current.value = newInputPrompt
    setEditorInput({
      text: newInputPrompt,
      htmlContent: newInputPrompt,
    })
  }

  const saveCaretPosition = (): void => {
    const rte = toolbarRef?.current
    if (rte && rte.formatter?.editorManager?.nodeSelection) {
      const range = rte.formatter.editorManager.nodeSelection.getRange(document)
      if (range && selectionRef) {
        selectionRef.current = range
      }
    }
  }

  const onTextChange = (): void => {
    if (toolbarRef?.current) {
      const _plainText = toolbarRef.current.getText()
      if (_plainText === '/') {
        setIsAiEnabled(true)
        setEditorInput({
          text: '',
          htmlContent: '',
        })
      } else {
        setEditorInput({
          text: _plainText,
          htmlContent: toolbarRef.current.getHtml(),
        })
      }
    }
    saveCaretPosition()
  }

  const onCreated = (): void => {
    requestComposerFocus()
    if (toolbarRef?.current) {
      const editorElement =
        toolbarRef.current.element?.querySelector('.e-content')
      if (editorElement) {
        editorElement.addEventListener('input', onTextChange)
        editorElement.addEventListener('paste', () =>
          setTimeout(onTextChange, 0),
        )
        editorElement.addEventListener('drop', () =>
          setTimeout(onTextChange, 0),
        )
      }
    }
  }

  const handleKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (autoFocusKey) e.stopPropagation()
      handleSend()
      requestComposerFocus()
    }
  }

  useEffect(() => {
    return () => {
      if (toolbarRef?.current) {
        const editorElement =
          toolbarRef.current.element?.querySelector('.e-content')
        if (editorElement) {
          editorElement.removeEventListener('input', onTextChange)
          editorElement.removeEventListener('paste', () =>
            setTimeout(onTextChange, 0),
          )
          editorElement.removeEventListener('drop', () =>
            setTimeout(onTextChange, 0),
          )
        }
      }
    }
  }, [])

  useEffect(() => {
    if (isEditing) {
      if (toolbarRef?.current) toolbarRef.current.value = editorInput.text
    }
  }, [isEditing, editorInput])

  useEffect(() => {
    const reference = toolbarRef?.current
    let refElement: Element | undefined = undefined

    if (reference && reference.contentModule) {
      refElement = reference.contentModule.getEditPanel?.()
      if (refElement) {
        refElement.addEventListener('keydown', handleKeyDown as EventListener)

        const editorRoot = reference.element
        if (editorRoot) {
          editorRoot.addEventListener('keydown', handleKeyDown as EventListener)
        }
      }
    }

    return () => {
      if (refElement) {
        refElement.removeEventListener(
          'keydown',
          handleKeyDown as EventListener,
        )
        const editorRoot = reference?.element
        if (editorRoot) {
          editorRoot.removeEventListener(
            'keydown',
            handleKeyDown as EventListener,
          )
        }
      }
    }
  }, [handleSend, autoFocusKey, requestComposerFocus, toolbarRef])

  return {
    handleAIFeaturesButtons,
    handleClearBtn,
    handleRightButtonsAction,
    handleToolbarActions,
    isRecording,
    mentionRef,
    onActionBegin,
    onCreated,
    onTextChange,
    peopleList,
    saveCaretPosition,
    setVariablePopupOpen,
    showVariables,
    toolbarRef,
    variableRef,
  }
}

export default useInlineTextEditor
